/**
 * The rate-limit hold: when the server answers 429, the button that caused it
 * stays inert and counts the seconds down (life-layer.md §2, "429 chip").
 *
 * The client does not decide how long: the caller passes the epoch-ms moment
 * the server said to come back (`Retry-After`), and this only turns it into a
 * whole-second countdown. The clock is read, never owned — the hold ends when
 * the time passes, not when a timer fires, so a backgrounded tab that wakes
 * late is released at once.
 */
import { computed, onScopeDispose, ref, watch, type Ref } from 'vue';

/** How often the displayed second is re-read; finer than 1 s so the digit never lags. */
const TICK_MS = 250;

/** Whole seconds left until `heldUntil` (rounded up so "0" is never shown while held); 0 when free. */
export function secondsLeft(heldUntil: number | undefined, now: number): number {
  if (heldUntil === undefined) return 0;
  return Math.max(0, Math.ceil((heldUntil - now) / 1000));
}

export interface Cooldown {
  /** True while the hold is in force. */
  held: Readonly<Ref<boolean>>;
  /** Seconds to show on the chip. */
  seconds: Readonly<Ref<number>>;
}

/** `getHeldUntil` is read reactively; `clock` is injectable so tests need no fake timers. */
export function useCooldown(getHeldUntil: () => number | undefined, clock: () => number = Date.now): Cooldown {
  const now = ref(clock());
  let timer: ReturnType<typeof setInterval> | null = null;

  function stop(): void {
    if (timer !== null) clearInterval(timer);
    timer = null;
  }

  function tick(): void {
    now.value = clock();
    if (secondsLeft(getHeldUntil(), now.value) === 0) stop();
  }

  watch(
    getHeldUntil,
    (until) => {
      stop();
      now.value = clock();
      if (secondsLeft(until, now.value) > 0) timer = setInterval(tick, TICK_MS);
    },
    { immediate: true },
  );
  onScopeDispose(stop);

  const seconds = computed(() => secondsLeft(getHeldUntil(), now.value));
  return { held: computed(() => seconds.value > 0), seconds };
}
